import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContactUsModel } from "./ContactUsModel";
import { ContactUSFormFields } from "./ContactUsModel";
import InputField from "../../core/InputField/InputField";
import Textarea from "../../core/Textarea/Textarea";
import SelectField from "../../core/SelectField/SelectField";

import "./ContactUs.css";
import SupportUtils from "../SupportUtils";
import { useNotification } from "../../core/Notification/NotificationProvider";
import ContactUsSuccess from "./ContactUsSuccess";
import { StoryblokComponent } from "@storyblok/react";
import { Theme } from "../../core/Models/CustomSiteConfigModel";
import { themeService } from "../../core/NotificationStripNav/ThemeService";

type ErrorFields = {
  [K in keyof ContactUSFormFields]?: string;
};

const ContactUs: React.FC<{ blok: ContactUsModel }> = ({ blok }) => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const maxFiles = 5;

  const [theme, setTheme] = useState<Theme | null>(themeService.getTheme());
  const [requestIsSent, setRequestIsSent] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<ErrorFields>({});
  const [values, setValues] = useState<ContactUSFormFields>({
    operatingSystemVersion: "",
    davAudioHost: "",
    help: { label: "", value: "" },
    subject: "",
    message: "",
    email: "",
  });

  const handleBackToSupportClick = () => {
    navigate(`/support/get-help`);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSelectChange = (
    e: { label: string; value: string },
    name: string
  ) => {
    setValues({
      ...values,
      [name]: e,
    });
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = (): ErrorFields => {
    const newErrors: ErrorFields = {};
    if (!values.operatingSystemVersion.trim()) {
      newErrors.operatingSystemVersion =
        "Operating System & Version is Required";
    }
    if (!values.email.trim()) {
      newErrors.email = "Emailul is Required";
    }
    if (!values.message.trim()) {
      newErrors.message = "Message is Required";
    }
    if (!values.subject.trim()) {
      newErrors.subject = "Subject is Required";
    }
    if (!values.help?.value.trim()) {
      newErrors.help = "How can we help is Required";
    }
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    SupportUtils.createTicket(values, files)
      .then((response) => {
        if (!response) {
          addNotification(
            "Sorry we couldn’t send your request right now. Please try again later",
            "error"
          );
        }

        setRequestIsSent(true);
      })
      .catch(() => {
        addNotification(
          "Sorry we couldn’t send your request right now. Please try again later",
          "error"
        );
      });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];

    if (selectedFiles.length + files.length > maxFiles) {
      alert(`You may select max ${maxFiles} files.`);
      return;
    }
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const unsubscribe = themeService.subscribe(setTheme);
    return () => unsubscribe();
  }, []);

  return (
    <div
      className="support-contact-us"
      style={{ backgroundColor: theme?.panelColour, color: theme?.textColor }}
    >
      <div className="container">
        <div className="support-title">
          <span className="back-to-support" onClick={handleBackToSupportClick}>
            BACK TO AUDIOMOVERS SUPPORT
          </span>
          <span> | {blok.title}</span>
        </div>
        <div className="sub-title">{blok.subTitle}</div>
        {!requestIsSent && <div className="standfirst">{blok.standfirst}</div>}
        {!requestIsSent && (
          <div className="support-contact-us-content">
            <form onSubmit={handleSubmit}>
              <div className="form-content">
                <InputField
                  label="Email Address"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  required={true}
                  error={errors.email}
                />
                <InputField
                  label="Operating System & Version"
                  name="operatingSystemVersion"
                  value={values.operatingSystemVersion}
                  onChange={handleChange}
                  required={true}
                  error={errors.operatingSystemVersion}
                />
                <InputField
                  label="DAW / Audio Host & Version"
                  name="davAudioHost"
                  value={values.davAudioHost}
                  onChange={handleChange}
                  required={false}
                  error={errors.davAudioHost}
                />
                <SelectField
                  label="How can we help?"
                  name="help"
                  value={values.help}
                  onChange={(e) => handleSelectChange(e, "help")}
                  options={SupportUtils.helpOptions}
                  error={errors.help}
                  required={true}
                />
                <InputField
                  label="Subject"
                  name="subject"
                  value={values.subject}
                  onChange={handleChange}
                  required={true}
                  error={errors.subject}
                  className="big-input"
                />
                <Textarea
                  label="Message"
                  name="message"
                  value={values.message}
                  onChange={handleChange}
                  required={true}
                  error={errors.message}
                  placeholder="Write your message here"
                />
                <div className="attach-container">
                  <label className="attach-file">
                    <img
                      className="attach-icon"
                      src={`${process.env.PUBLIC_URL}/images/attach.png`}
                      alt="icon"
                    />
                    <span className="attach-text">
                      Attach files (max {maxFiles})
                    </span>
                    <input
                      type="file"
                      className="file-input"
                      multiple
                      onChange={handleFileChange}
                    />
                  </label>
                  <ul>
                    {files.map((file, index) => (
                      <li
                        key={index}
                        className="attach-file-name"
                        onClick={() => removeFile(index)}
                      >
                        {file.name}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="button-container">
                  <button type="submit" className="button">
                    SEND MESSAGE
                  </button>
                </div>
              </div>
            </form>
            <div className="level-jorney-container">
              {blok.levelJourneys &&
                blok.levelJourneys.map((levelJourney) => (
                  <StoryblokComponent
                    blok={levelJourney}
                    key={levelJourney._uid}
                  />
                ))}
            </div>
          </div>
        )}
        {requestIsSent && <ContactUsSuccess></ContactUsSuccess>}
      </div>
    </div>
  );
};

export default ContactUs;
